/**
 * <rlhf-dimension-grader> — grade ONE rubric dimension of a subject.
 *
 * Shows the dimension title, its 1/3/5 anchor descriptions (collapsible), a 1–5
 * score picker, and a rationale field; on save it appends a `grade.Recorded` event
 * into the injected SessionStore. When external judge verdicts are provided they
 * render side-by-side with the operator's pick, and any judge that disagrees by
 * more than one point is highlighted — the `revealJudges` flag controls whether
 * they show before the operator grades (calibration) or only after (blind-first).
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import type { DimensionGrade, JudgeVerdictRef, RubricDimension, SessionState } from "@platform/rlhf-core"
import { RlhfSessionStoreToken, type RlhfSessionStore } from "./store"
import { RlhfDraftStoreToken, optionalDraftStore, type RlhfDraftStore } from "./drafts"
import { systemClock, type RlhfClock } from "./clock"
import { seamAutoGrow } from "./seam"

const SCORES = [1, 2, 3, 4, 5] as const

/** The rubric caption shown under the scale for a hovered/selected score. Anchors
 *  are defined for 1/3/5; the even rungs read as »between« the neighbours. */
function anchorCaption(dim: RubricDimension, n: number): TemplateResult {
  const label = (s: "1" | "3" | "5"): string => dim.anchors[s]
  switch (n) {
    case 1:
      return html`<b>1</b> — ${label("1")}`
    case 2:
      return html`<b>2</b> — zwischen Stufe 1 und 3`
    case 3:
      return html`<b>3</b> — ${label("3")}`
    case 4:
      return html`<b>4</b> — zwischen Stufe 3 und 5`
    default:
      return html`<b>5</b> — ${label("5")}`
  }
}

export class DimensionGraderVm implements ViewModel {
  readonly score: Signal<number | null> = signal<number | null>(null)
  readonly rationale: Signal<string> = signal("")
  /** True once an unsaved rationale draft was restored from storage (shows the hint). */
  readonly draftRestored: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  private readonly storeState: ReadonlySignal<SessionState>

  constructor(
    private readonly store: RlhfSessionStore,
    readonly dimension: ReadonlySignal<RubricDimension | null>,
    readonly subjectRef: ReadonlySignal<string>,
    readonly judges: ReadonlySignal<readonly JudgeVerdictRef[]>,
    readonly revealJudges: ReadonlySignal<boolean>,
    private readonly actor: ReadonlySignal<string>,
    private readonly clock: RlhfClock,
    private readonly drafts: RlhfDraftStore | null = null,
  ) {
    this.storeState = store.state()
    this.renderDeps = [
      dimension,
      subjectRef,
      judges,
      revealJudges,
      this.score,
      this.rationale,
      this.draftRestored,
      this.storeState,
    ]
  }

  /** The per-(subject,dimension) key an unsaved rationale draft is stored under. */
  private draftField(): string | null {
    const dim = this.dimension.value
    return dim ? `grade:${this.subjectRef.value}:${dim.id}:rationale` : null
  }

  attach(): () => void {
    const existing = this.existingGrade()
    const field = this.draftField()
    if (existing) {
      // A SAVED grade is authoritative — prefill from it and drop any stale draft.
      this.score.set(existing.score)
      this.rationale.set(existing.rationale)
      if (field) this.drafts?.clear(field)
    } else if (field) {
      // No saved grade yet — restore an in-progress rationale if one survived a reload.
      const draft = this.drafts?.read(field)
      if (draft) {
        this.rationale.set(draft)
        this.draftRestored.set(true)
      }
    }
    return () => {}
  }

  /** The grade already recorded for this subject+dimension, if any. */
  existingGrade(): DimensionGrade | undefined {
    const dim = this.dimension.value
    if (!dim) return undefined
    const ref = this.subjectRef.value
    return this.storeState.value.grades.find((g) => g.subjectRef === ref && g.dimension === dim.id)
  }

  selectScore(n: number): void {
    this.score.set(n)
  }
  setRationale(text: string): void {
    this.rationale.set(text)
    const field = this.draftField()
    if (field) this.drafts?.write(field, text)
  }

  /** Judges show when revealed, or once the operator has recorded a grade (blind-first). */
  judgesVisible(): boolean {
    return this.revealJudges.value || this.existingGrade() !== undefined
  }

  /** Judge indices whose score disagrees with the current pick by more than 1 point. */
  disagreeing(): Set<number> {
    const picked = this.score.value
    const out = new Set<number>()
    if (picked === null) return out
    this.judges.value.forEach((j, i) => {
      if (Math.abs(j.score - picked) > 1) out.add(i)
    })
    return out
  }

  /** Append the grade. No-op without both a score and a rationale. */
  save(): void {
    const dim = this.dimension.value
    const s = this.score.value
    const r = this.rationale.value.trim()
    if (!dim || s === null || r.length === 0) return
    this.store.append({
      type: "grade.Recorded",
      at: this.clock.now(),
      actor: this.actor.value,
      grade: { subjectRef: this.subjectRef.value, dimension: dim.id, score: s, rationale: r },
    })
    // Saved — the durable event supersedes the pre-save draft.
    const field = this.draftField()
    if (field) this.drafts?.clear(field)
    this.draftRestored.set(false)
  }
}

export class RlhfDimensionGrader extends KitElement<DimensionGraderVm> {
  readonly #dimension = signal<RubricDimension | null>(null)
  readonly #subjectRef = signal("")
  readonly #judges = signal<readonly JudgeVerdictRef[]>([])
  readonly #revealJudges = signal(false)
  readonly #actor = signal("operator")
  #clock: RlhfClock = systemClock()

  get dimension(): RubricDimension | null {
    return this.#dimension.value
  }
  set dimension(v: RubricDimension | null) {
    this.#dimension.set(v)
  }
  get subjectRef(): string {
    return this.#subjectRef.value
  }
  set subjectRef(v: string) {
    this.#subjectRef.set(v)
  }
  get judges(): readonly JudgeVerdictRef[] {
    return this.#judges.value
  }
  set judges(v: readonly JudgeVerdictRef[]) {
    this.#judges.set(v)
  }
  get revealJudges(): boolean {
    return this.#revealJudges.value
  }
  set revealJudges(v: boolean) {
    this.#revealJudges.set(v)
  }
  get actor(): string {
    return this.#actor.value
  }
  set actor(v: string) {
    this.#actor.set(v)
  }
  set clock(c: RlhfClock) {
    this.#clock = c
  }

  protected createViewModel(ctx: KitContext): DimensionGraderVm {
    return new DimensionGraderVm(
      ctx.inject(RlhfSessionStoreToken),
      this.#dimension,
      this.#subjectRef,
      this.#judges,
      this.#revealJudges,
      this.#actor,
      this.#clock,
      optionalDraftStore(ctx),
    )
  }

  protected render(vm: DimensionGraderVm): TemplateResult {
    const dim = vm.dimension.value
    if (!dim) return html`${nothing}`
    const picked = vm.score.value
    const isGate = dim.kind === "gate"
    const titleId = `rlhf-grader-${dim.id}`
    return html`
      <section
        class=${classMap({ "rlhf-grader": true, [`is-${dim.kind}`]: true })}
        data-dimension=${dim.id}
        aria-labelledby=${titleId}
      >
        <div class="rlhf-grader-head">
          <h3 id=${titleId} class="rlhf-grader-title" lang="de">
            <span class="rlhf-grader-id">${dim.id}</span>
            <span class="rlhf-grader-name">${dim.title}</span>
          </h3>
          ${isGate
            ? html`<span class="rlhf-grader-badge" lang="de" title="Hard-Fail-Dimension">Gate</span>`
            : nothing}
        </div>

        <div class="rlhf-rating">
          <div class="rlhf-scale" role="radiogroup" aria-label=${`Note für ${dim.id} — ${dim.title}`}>
            ${SCORES.map(
              (n) => html`<button
                type="button"
                role="radio"
                class="rlhf-scale-opt"
                data-act="grade-score"
                data-score=${String(n)}
                aria-checked=${picked === n ? "true" : "false"}
                aria-label=${`Note ${n} von 5`}
                lang="de"
              >
                ${n}
              </button>`,
            )}
          </div>
          <div class="rlhf-anchor-cap" aria-live="polite">
            ${SCORES.map(
              (n) => html`<p class="rlhf-anchor" data-for=${String(n)} lang="de">${anchorCaption(dim, n)}</p>`,
            )}
          </div>
        </div>

        <label class="rlhf-field rlhf-rationale" lang="de">
          <span class="rlhf-field-label" lang="de">Begründung</span>
          <textarea
            class="rlhf-seam"
            rows="1"
            ${seamAutoGrow()}
            placeholder="Warum diese Note? Kurz gegen die Anker begründen."
            .value=${vm.rationale.value}
            @input=${(e: Event) => vm.setRationale((e.target as HTMLTextAreaElement).value)}
            lang="de"
          ></textarea>
          ${vm.draftRestored.value
            ? html`<span class="rlhf-draft-hint" role="status" lang="de">Entwurf wiederhergestellt</span>`
            : nothing}
        </label>

        <div class="rlhf-grader-actions">
          <button type="button" class="rlhf-btn rlhf-btn-primary" data-act="grade-save" lang="de">
            Bewertung speichern
          </button>
        </div>

        ${vm.judgesVisible() && vm.judges.value.length > 0
          ? html`<div class="rlhf-judges" aria-label="Judge-Urteile">
              <p class="rlhf-judges-head" lang="de">Judge-Urteile</p>
              ${vm.judges.value.map((j) => {
                const delta = picked === null ? null : Math.abs(j.score - picked)
                const disagree = delta !== null && delta > 1
                return html`<div
                  class=${classMap({ "rlhf-judge": true, "is-disagree": disagree })}
                  lang="de"
                >
                  <div class="rlhf-judge-head">
                    <span class="rlhf-judge-model">${j.judgeModel}</span>
                    <span class="rlhf-judge-badge">${j.score}/5</span>
                    ${disagree
                      ? html`<span class="rlhf-judge-delta" title=${`${delta} Punkte von deiner Note entfernt`}
                          >Δ${delta}</span
                        >`
                      : nothing}
                  </div>
                  <p class="rlhf-judge-quote" lang="de">${j.rationale}</p>
                </div>`
              })}
            </div>`
          : nothing}
      </section>
    `
  }

  protected override actions(vm: DimensionGraderVm): ActionMap {
    return {
      "grade-score": ({ score }) => {
        if (score) vm.selectScore(Number(score))
      },
      "grade-save": () => vm.save(),
    }
  }
}
